/**
 * Dynamic Query Key Registry
 *
 * This module automatically builds a hierarchical mapping of query keys
 * by monitoring TanStack Query's internal store, eliminating the need
 * for manual pattern maintenance.
 *
 * The registry maintains a tree structure like:
 * {
 *   customers: {
 *     all: true,
 *     "123": true
 *   },
 *   products: {
 *     all: true,
 *     "with-count": true,
 *     "456": true
 *   }
 * }
 *
 * Memory Management:
 * - Uses in-memory storage (not localStorage) for performance
 * - Registry persists for the app session
 * - Automatically rebuilds from existing queries on initialization
 * - Lightweight tree structure for fast lookups
 */
import type { QueryClient } from '@tanstack/react-query';

interface QueryKeyNode {
  [key: string]: QueryKeyNode | boolean;
}

export class QueryKeyRegistry {
  private registry: QueryKeyNode = {};
  private queryClient: QueryClient | null = null;
  private isInitialized = false;

  /**
   * Initialize the registry with TanStack Query client
   */
  initialize(queryClient: QueryClient) {
    if (this.isInitialized) return;

    this.queryClient = queryClient;
    this.buildRegistryFromExistingQueries();
    this.isInitialized = true;
  }

  /**
   * Build registry from existing queries in TanStack Query cache
   */
  private buildRegistryFromExistingQueries() {
    if (!this.queryClient) return;

    try {
      const cache = this.queryClient.getQueryCache();
      const queries = cache.getAll();

      queries.forEach((query) => {
        const queryKey = query.queryKey;
        if (Array.isArray(queryKey) && queryKey.length > 0) {
          this.addQueryKey(queryKey[0] as string);
        }
      });
    } catch (error) {
      console.warn('Failed to build registry from existing queries:', error);
    }
  }

  /**
   * Add a query key to the registry
   */
  addQueryKey(queryKey: string) {
    if (!queryKey || typeof queryKey !== 'string') return;

    const parts = queryKey.split('/').filter((part) => part.trim().length > 0);
    if (parts.length === 0) return;

    let current = this.registry;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      // If the current part doesn't exist, create it
      if (!current[part]) {
        current[part] = i === parts.length - 1 ? true : {};
      }
      // If the current part exists but is a boolean (leaf node) and we need to add children
      else if (current[part] === true && i < parts.length - 1) {
        // Convert the boolean leaf to an object to allow children
        current[part] = {};
      }

      // Move to the next level
      current = current[part] as QueryKeyNode;
    }
  }

  /**
   * Get all query keys that should be invalidated for a given mutation route
   * Uses wildcard matching to find all related queries
   */
  getInvalidationKeys(mutationRoute: string): string[] {
    if (!mutationRoute || typeof mutationRoute !== 'string') return [];

    const parts = mutationRoute.split('/').filter((part) => part.length > 0);
    if (parts.length === 0) return [];

    const rootResource = parts[0];

    // Use wildcard matching to find all queries starting with the root resource
    const wildcardMatches = this.findQueriesMatchingPattern(`${rootResource}*`);

    return [...new Set(wildcardMatches)]; // Remove duplicates
  }

  /**
   * Invalidate queries matching multiple wildcard patterns
   * @param patterns Array of wildcard patterns (e.g., ["wedding/guest*", "wedding/insights*"])
   * @param queryClient TanStack Query client instance
   */
  invalidateQueriesByPatterns(patterns: string[], queryClient: QueryClient) {
    const allMatches: string[] = [];

    patterns.forEach(pattern => {
      const matches = this.findQueriesMatchingPattern(pattern);
      allMatches.push(...matches);
    });

    // Remove duplicates and invalidate
    const uniqueKeys = [...new Set(allMatches)];
    uniqueKeys.forEach(key => {
      queryClient.invalidateQueries({ queryKey: [key] });
    });
  }

  /**
   * Find all queries matching a wildcard pattern (e.g., 'orders*')
   */
  public findQueriesMatchingPattern(pattern: string): string[] {
    const matches: string[] = [];
    const prefix = pattern.replace('*', ''); // Remove wildcard to get prefix

    // Traverse the registry to find all queries starting with the prefix
    this.traverseRegistry(this.registry, '', (fullPath) => {
      if (fullPath.startsWith(prefix)) {
        matches.push(fullPath);
      }
    });

    return matches;
  }

  /**
   * Traverse the entire registry and call callback for each path
   */
  private traverseRegistry(
    node: QueryKeyNode,
    currentPath: string,
    callback: (fullPath: string) => void,
  ) {
    Object.keys(node).forEach((key) => {
      const fullPath = currentPath ? `${currentPath}/${key}` : key;

      // Call the callback for this path
      callback(fullPath);

      // Recursively traverse children
      const childNode = node[key];
      if (typeof childNode === 'object') {
        this.traverseRegistry(childNode, fullPath, callback);
      }
    });
  }

  /**
   * Get the current registry state (for debugging)
   */
  getRegistry() {
    return this.registry;
  }

  /**
   * Get registry statistics
   */
  getStats() {
    const stats = {
      totalResources: Object.keys(this.registry).length,
      totalPaths: 0,
      isInitialized: this.isInitialized,
    };

    const countPaths = (node: QueryKeyNode, prefix: string = '') => {
      Object.keys(node).forEach((key) => {
        stats.totalPaths++;
        const childNode = node[key];
        if (typeof childNode === 'object') {
          countPaths(childNode, `${prefix}/${key}`);
        }
      });
    };

    countPaths(this.registry);
    return stats;
  }

  /**
   * Clear the registry (useful for testing)
   */
  clear() {
    this.registry = {};
    this.isInitialized = false;
  }
}

// Singleton instance - persists for the app session
export const queryKeyRegistry = new QueryKeyRegistry();

/**
 * Hook to initialize the registry with query client
 */
export const useQueryKeyRegistry = (queryClient: QueryClient) => {
  queryKeyRegistry.initialize(queryClient);
  return queryKeyRegistry;
};

/**
 * Get invalidation keys for a mutation route
 */
export const getDynamicInvalidationKeys = (mutationRoute: string): string[] => {
  return queryKeyRegistry.getInvalidationKeys(mutationRoute);
};
